import { PAGINATION_ORDER } from 'shared/constants'
import { CommentInterface } from 'modules/comments/components/comment/comment.types'
import { CommentSortEnum, CommentSortOptions } from 'modules/comments/types'

export const getDefaultSortOption = (isCommunityComments: boolean): CommentSortEnum => {
  return isCommunityComments ? CommentSortOptions.OLDEST_FIRST : CommentSortOptions.NEWEST_FIRST
}

/**
 * Calculates comment popularity based on likes count and replies count
 * Popularity = likesCount + repliesCount (with alpha = 1)
 */
export const calculateCommentPopularity = (comment: CommentInterface): number => {
  return comment.likesCount + comment.totalReplies
}

export const getCommentSortParams = (
  sortOption: CommentSortEnum,
): {
  sortBy: string
  sortOrder: 'desc' | 'asc'
} => {
  const sortOrderForAPI = isOldestFirst(sortOption) ? PAGINATION_ORDER.ASC : PAGINATION_ORDER.DESC

  return {
    sortBy: isPopularSort(sortOption) ? 'popularity' : 'id',
    sortOrder: isPopularSort(sortOption) ? PAGINATION_ORDER.DESC : sortOrderForAPI,
  }
}

export const createCommentSortComparator = (
  sortOption: CommentSortEnum,
): ((a: CommentInterface, b: CommentInterface) => number) => {
  return (a: CommentInterface, b: CommentInterface): number => {
    switch (sortOption) {
      case CommentSortOptions.POPULAR: {
        const popularityA = calculateCommentPopularity(a)
        const popularityB = calculateCommentPopularity(b)
        // Sort by popularity DESC, fallback to newest first if popularity is equal
        if (popularityA !== popularityB) {
          return popularityB - popularityA
        }
        return b.id - a.id
      }
      case CommentSortOptions.NEWEST_FIRST: {
        return b.id - a.id
      }
      case CommentSortOptions.OLDEST_FIRST: {
        return a.id - b.id
      }
      default: {
        return b.id - a.id
      }
    }
  }
}

export const sortComments = (
  comments: CommentInterface[],
  sortOption: CommentSortEnum,
): CommentInterface[] => {
  return [...comments].sort(createCommentSortComparator(sortOption))
}

export const isCommentBeforeWithObjects = (
  comment: CommentInterface,
  targetComment: CommentInterface,
  sortOption: CommentSortEnum,
): boolean => {
  switch (sortOption) {
    case CommentSortOptions.POPULAR: {
      const commentPopularity = calculateCommentPopularity(comment)
      const targetPopularity = calculateCommentPopularity(targetComment)
      if (commentPopularity !== targetPopularity) {
        return commentPopularity > targetPopularity
      }
      // If popularity is equal, fallback to newest first
      return comment.id > targetComment.id
    }
    case CommentSortOptions.NEWEST_FIRST: {
      return comment.id > targetComment.id
    }
    case CommentSortOptions.OLDEST_FIRST: {
      return comment.id < targetComment.id
    }
    default: {
      return comment.id > targetComment.id
    }
  }
}

export const isNewestFirst = (sortOption: CommentSortEnum): boolean => {
  return sortOption === CommentSortOptions.NEWEST_FIRST
}

export const isOldestFirst = (sortOption: CommentSortEnum): boolean => {
  return sortOption === CommentSortOptions.OLDEST_FIRST
}

export const isPopularSort = (sortOption: CommentSortEnum): boolean => {
  return sortOption === CommentSortOptions.POPULAR
}
